/**
 * Keeping the page's lists and the shared store in step — pure, so the rules
 * are tested without a server.
 *
 * Each list keeps a ledger: per id, the JSON last sent to (or received from)
 * the store. An item whose JSON differs from its ledger entry has changes the
 * store hasn't seen, and goes up; a store copy replaces the page's only when
 * the page has nothing unsent for it, so a reply being scored right now is
 * never overwritten by an older copy of itself.
 */

export type Ledger = Map<string, string>;

export interface RemoteMeta {
  /** Who last wrote it (a Kratos identity, or `scheduler`). */
  _by?: string | null;
  _updatedAt?: number;
}

const strip = <T extends { id: string }>(remote: T & RemoteMeta): T => {
  const { _by: _b, _updatedAt: _u, ...item } = remote;
  return item as unknown as T;
};

export const jsonOf = (item: unknown) => JSON.stringify(item);

export interface MergeResult<T> {
  list: T[];
  /** Who wrote each item the store sent, by id. */
  by: Map<string, string>;
}

/**
 * Items the page has but has never synced, which the store has too, where
 * the store's copy should win (`prefer`). Decided before the merge, so the
 * merge itself gives the same answer however often React runs it.
 *
 * Without this the page's copy wins, which is right for runs (each browser
 * makes its own), but not for judges, which every solution starts with
 * under the same ids — a browser opening the solution for the first time
 * mustn't push its defaults over the team's.
 */
export const takeOnFirstContact = <T extends { id: string }>(
  local: T[],
  remote: T[],
  {
    ledger,
    prefer,
  }: { ledger: Ledger; prefer: (mine: T, theirs: T) => boolean }
): Set<string> => {
  const mine = new Map(local.map((item) => [item.id, item]));
  const ids = new Set<string>();
  remote.forEach((theirs) => {
    const m = mine.get(theirs.id);
    if (m && !ledger.has(theirs.id) && prefer(m, strip(theirs))) {
      ids.add(theirs.id);
    }
  });
  return ids;
};

/**
 * The store's copies merged into the page's list. `takeRemote` names items
 * whose store copy wins even though the page's looks unsent
 * (`takeOnFirstContact`).
 */
export const mergeRemote = <T extends { id: string }>(
  local: T[],
  remote: (T & RemoteMeta)[],
  {
    ledger,
    takeRemote = new Set(),
  }: { ledger: Ledger; takeRemote?: Set<string> }
): MergeResult<T> => {
  const byId = new Map(local.map((item) => [item.id, item]));
  const by = new Map<string, string>();
  remote.forEach((raw) => {
    const item = strip(raw);
    if (raw._by) by.set(item.id, raw._by);
    const json = jsonOf(item);
    const mine = byId.get(item.id);
    const fresh = takeRemote.has(item.id);
    const unsent =
      !fresh && mine !== undefined && ledger.get(item.id) !== jsonOf(mine);
    if (!mine || (!unsent && jsonOf(mine) !== json)) {
      byId.set(item.id, item);
    }
    if (!unsent) ledger.set(item.id, json);
  });
  return { list: [...byId.values()], by };
};

/** Items the store hasn't seen as they are now. */
export const unsent = <T extends { id: string }>(
  list: T[],
  ledger: Ledger
): T[] => list.filter((item) => ledger.get(item.id) !== jsonOf(item));

/** Ids the store has that the page has dropped: deletions to send. */
export const dropped = (
  list: { id: string }[],
  known: Set<string>
): string[] => {
  const present = new Set(list.map((x) => x.id));
  return [...known].filter((id) => !present.has(id));
};

export const chunks = <T>(list: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(list.length / size) }, (_, i) =>
    list.slice(i * size, i * size + size)
  );

/** Most records one store put carries. */
export const PUT_BATCH_RECORDS = 25;

/**
 * Most bytes of records one store put carries. The server caps an /ai/* body
 * at 512 KB (`DEFAULT_BODY_BYTES` in `bobcat/src/server-only/ai/limits.ts`); this leaves
 * room for the envelope (scope, collection, ids, timestamps) around them.
 */
export const PUT_BATCH_BYTES = 400_000;

const byteLength = (text: string) => new TextEncoder().encode(text).length;

/**
 * Store puts, at most `PUT_BATCH_RECORDS` records and `PUT_BATCH_BYTES` each,
 * in order. A record over the byte budget alone goes in a put of its own: the
 * store then refuses that record, not the ones around it.
 */
export const putBatches = <T>(items: T[]): T[][] => {
  const batches: T[][] = [];
  let batch: T[] = [];
  let bytes = 0;
  items.forEach((item) => {
    const size = byteLength(jsonOf(item));
    if (
      batch.length &&
      (batch.length >= PUT_BATCH_RECORDS || bytes + size > PUT_BATCH_BYTES)
    ) {
      batches.push(batch);
      batch = [];
      bytes = 0;
    }
    batch.push(item);
    bytes += size;
  });
  if (batch.length) batches.push(batch);
  return batches;
};
